import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import {
  CreditCard,
  Calendar,
  DollarSign,
  Pause,
  Play,
  X,
  RefreshCw,
  FileText,
  Settings,
  Receipt,
  Wallet,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Download,
  Eye,
  Plus
} from 'lucide-react';
import {
  getStripeCustomerDetails,
  adminChangeUserPlan,
  adminPauseSubscription,
  adminResumeSubscription,
  adminCancelSubscription,
  getUserBillingSummary,
  getAvailableStripePlans,
  getCustomerInvoices,
  adminCreateInvoice,
  adminRefundPayment,
  adminApplyDiscount,
  adminUpdateBillingCycle,
  StripeCustomerDetails,
  StripeSubscription,
  StripeInvoice
} from '@/api/stripe-admin';
import { createStripeCustomer } from '@/api/stripe';

interface StripeUserManagementProps {
  user: {
    id: string;
    email: string;
    name: string;
    stripe_customer_id?: string;
    subscription_status?: string;
    current_period_end?: string;
  };
  onUserUpdate?: () => void;
}

export const StripeUserManagement: React.FC<StripeUserManagementProps> = ({ user, onUserUpdate }) => {
  const [customerDetails, setCustomerDetails] = useState<StripeCustomerDetails | null>(null);
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(!!user.stripe_customer_id);
  const { toast } = useToast();

  // Load all Stripe data if customer ID exists
  useEffect(() => {
    // Sync local state with user prop
    setHasStripeCustomer(!!user.stripe_customer_id);

    if (user.stripe_customer_id) {
      loadAllStripeData();
    }
    loadAvailablePlans();
  }, [user.stripe_customer_id]);

  // Load all Stripe data for the customer
  const loadAllStripeData = async () => {
    if (!user.stripe_customer_id) return;

    setLoading(true);
    try {
      // Load customer details and invoices in parallel
      const [customerData, invoiceData] = await Promise.all([
        getStripeCustomerDetails(user.stripe_customer_id),
        getCustomerInvoices(user.stripe_customer_id)
      ]);

      setCustomerDetails(customerData);
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error loading Stripe data:', error);
      toast({
        title: "Error",
        description: "Failed to load Stripe data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh all data
  const refreshAllData = async () => {
    setRefreshing(true);
    await loadAllStripeData();
    setRefreshing(false);
  };

  const loadCustomerDetails = async () => {
    if (!user.stripe_customer_id) return;

    setLoading(true);
    try {
      const details = await getStripeCustomerDetails(user.stripe_customer_id);
      setCustomerDetails(details);
    } catch (error) {
      console.error('Error loading customer details:', error);
      toast({
        title: "Error",
        description: "Failed to load Stripe customer details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePlans = async () => {
    try {
      console.log('Loading available Stripe plans...');
      const plans = await getAvailableStripePlans();
      console.log('Available plans loaded:', plans);
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Error loading Stripe plans:', error);
      // Fallback to empty array if plans can't be loaded
      setAvailablePlans([]);
    }
  };

  const handleCreateStripeCustomer = async () => {
    setLoading(true);
    try {
      console.log('Creating Stripe customer for:', user.email, user.name);

      toast({
        title: "Creating Customer",
        description: "Creating Stripe customer profile...",
      });

      // Create the Stripe customer
      const result = await createStripeCustomer(user.email, user.name);
      console.log('Stripe customer created:', result);

      // Update the user object locally with the new customer ID
      user.stripe_customer_id = result.customer_id;

      // Update local state to trigger re-render
      setHasStripeCustomer(true);

      toast({
        title: "Loading Data",
        description: "Customer created! Loading billing interface...",
      });

      // Load the Stripe data for the new customer
      try {
        const [customerData, invoiceData] = await Promise.all([
          getStripeCustomerDetails(result.customer_id),
          getCustomerInvoices(result.customer_id)
        ]);

        // Update component state with the new data
        setCustomerDetails(customerData);
        setInvoices(invoiceData);

        toast({
          title: "Success!",
          description: "Stripe customer created and billing interface loaded!",
        });

      } catch (dataError) {
        console.error('Error loading Stripe data for new customer:', dataError);
        // Even if data loading fails, we still have a customer created
        toast({
          title: "Customer Created",
          description: "Customer created successfully. Please refresh to see billing data.",
          variant: "default",
        });
      }

      // Notify parent component to refresh user data in the background
      onUserUpdate?.();

    } catch (error: any) {
      console.error('Error creating Stripe customer:', error);

      const errorMessage = error.message || error.error?.message || 'Failed to create Stripe customer';

      toast({
        title: "Error Creating Customer",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlan || !customerDetails) return;

    setLoading(true);
    try {
      console.log('Changing plan for customer:', customerDetails.id, 'to plan:', selectedPlan);

      // Find the selected plan details
      const selectedPlanDetails = availablePlans.find(plan => plan.id === selectedPlan);
      if (!selectedPlanDetails) {
        throw new Error('Selected plan not found');
      }

      const activeSubscription = customerDetails.subscriptions.data.find(sub =>
        ['active', 'trialing', 'past_due'].includes(sub.status)
      );

      if (activeSubscription) {
        // Update existing subscription
        console.log('Updating existing subscription:', activeSubscription.id);
        const result = await adminChangeUserPlan(customerDetails.id, selectedPlan);

        toast({
          title: "Success",
          description: `Subscription changed to ${selectedPlanDetails.nickname || 'new plan'}. ${result?.message || ''}`,
        });
      } else {
        // No active subscription - would need to create one
        toast({
          title: "No Active Subscription",
          description: "This customer has no active subscription. Use Stripe dashboard to create a new subscription.",
          variant: "destructive",
        });
        return;
      }

      // Close dialog and refresh data
      setShowPlanDialog(false);
      setSelectedPlan('');

      // Refresh all Stripe data to show changes
      await loadAllStripeData();

      // Notify parent component of update
      onUserUpdate?.();

    } catch (error: any) {
      console.error('Error changing plan:', error);

      // Show specific error message if available
      const errorMessage = error.message || error.error?.message || 'Failed to change subscription plan';

      toast({
        title: "Error Changing Plan",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePauseSubscription = async (subscriptionId: string) => {
    setLoading(true);
    try {
      await adminPauseSubscription(subscriptionId);
      toast({
        title: "Success",
        description: "Subscription paused successfully",
      });
      loadCustomerDetails();
      onUserUpdate?.();
    } catch (error) {
      console.error('Error pausing subscription:', error);
      toast({
        title: "Error",
        description: "Failed to pause subscription",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    setLoading(true);
    try {
      await adminResumeSubscription(subscriptionId);
      toast({
        title: "Success",
        description: "Subscription resumed successfully",
      });
      loadCustomerDetails();
      onUserUpdate?.();
    } catch (error) {
      console.error('Error resuming subscription:', error);
      toast({
        title: "Error",
        description: "Failed to resume subscription",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    setLoading(true);
    try {
      await adminCancelSubscription(subscriptionId, true); // Cancel at period end
      toast({
        title: "Success",
        description: "Subscription will be canceled at period end",
      });
      loadCustomerDetails();
      onUserUpdate?.();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatAmount = (amount: number, currency?: string) => {
    const safeCurrency = currency || 'usd';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCurrency.toUpperCase(),
    }).format((amount || 0) / 100);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      incomplete: 'bg-orange-100 text-orange-800',
      incomplete_expired: 'bg-red-100 text-red-800',
      trialing: 'bg-blue-100 text-blue-800',
      paused: 'bg-gray-100 text-gray-800',
      paid: 'bg-green-100 text-green-800',
      open: 'bg-blue-100 text-blue-800',
      void: 'bg-gray-100 text-gray-800',
      draft: 'bg-gray-100 text-gray-800',
      uncollectible: 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };



  if (!user.stripe_customer_id && !hasStripeCustomer) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Stripe Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            This user doesn't have a Stripe customer profile yet.
          </p>
          <Button onClick={handleCreateStripeCustomer} disabled={loading}>
            {loading ? "Creating..." : "Create Stripe Customer"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Stripe Billing Management</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshAllData}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-20">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            Loading Stripe data...
          </CardContent>
        </Card>
      ) : customerDetails ? (
        <div className="space-y-6">
          {/* Customer Information & Admin Actions - Combined Section */}
 

          {/* Overview Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold">{customerDetails.subscriptions.data.length}</p>
                    <p className="text-sm text-gray-600">Subscriptions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <Receipt className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold">{invoices.length}</p>
                    <p className="text-sm text-gray-600">Invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <Wallet className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold">{(customerDetails as any).payment_methods?.data?.length || 0}</p>
                    <p className="text-sm text-gray-600">Payment Methods</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-8 w-8 text-orange-600" />
                  <div>
                    <p className="text-2xl font-bold">
                      {formatAmount(customerDetails.balance, customerDetails.currency || 'usd')}
                    </p>
                    <p className="text-sm text-gray-600">Account Balance</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>



          {/* Subscriptions Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  <span>Subscriptions ({customerDetails.subscriptions.data.length})</span>
                </div>
                <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
                  <DialogTrigger asChild>
                    
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Change Subscription Plan</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Current Plan Info */}
                      {customerDetails.subscriptions.data.length > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Current Plan:</p>
                          <p className="text-sm text-gray-600">
                            {customerDetails.subscriptions.data[0].plan.nickname} - {' '}
                            {formatAmount(customerDetails.subscriptions.data[0].plan.amount, customerDetails.subscriptions.data[0].plan.currency)} / {customerDetails.subscriptions.data[0].plan.interval}
                          </p>
                        </div>
                      )}

                      {/* Plan Selection */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Select New Plan:
                        </label>
                        <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a plan..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePlans.map((plan) => (
                              <SelectItem key={plan.id} value={plan.id}>
                                <div className="flex flex-col">
                                  <span className="font-medium">{plan.nickname}</span>
                                  <span className="text-sm text-gray-500">
                                    {formatAmount(plan.amount, plan.currency)} / {plan.interval}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Selected Plan Preview */}
                      {selectedPlan && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-medium text-blue-700">New Plan:</p>
                          {(() => {
                            const plan = availablePlans.find(p => p.id === selectedPlan);
                            return plan ? (
                              <p className="text-sm text-blue-600">
                                {plan.nickname} - {formatAmount(plan.amount, plan.currency)} / {plan.interval}
                              </p>
                            ) : null;
                          })()}
                        </div>
                      )}

                      {/* Warning about plan changes */}
                      {selectedPlan && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div className="text-sm text-yellow-700">
                              <p className="font-medium">Plan Change Notice:</p>
                              <p>This will immediately change the subscription and may create prorations. The customer will be charged/credited for the difference.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleChangePlan}
                          disabled={!selectedPlan || loading}
                          className="flex-1"
                        >
                          {loading ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Changing Plan...
                            </>
                          ) : (
                            'Confirm Plan Change'
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowPlanDialog(false);
                            setSelectedPlan('');
                          }}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customerDetails.subscriptions.data.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No active subscriptions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customerDetails.subscriptions.data.map((subscription: StripeSubscription) => (
                    <div key={subscription.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(subscription.status)}>
                            {subscription.status}
                          </Badge>
                          <div>
                            <p className="font-medium">{subscription.plan.nickname}</p>
                            <p className="text-sm text-gray-600">
                              {formatAmount(subscription.plan.amount, subscription.plan.currency)} / {subscription.plan.interval}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {subscription.status === 'active' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePauseSubscription(subscription.id)}
                              disabled={loading}
                            >
                              <Pause className="h-4 w-4 mr-2" />
                              Pause
                            </Button>
                          )}
                          {subscription.status === 'paused' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResumeSubscription(subscription.id)}
                              disabled={loading}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Resume
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelSubscription(subscription.id)}
                            disabled={loading}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-500">Subscription ID</p>
                          <p className="font-mono">{subscription.id}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Current Period</p>
                          <p>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Cancel at Period End</p>
                          <p>{subscription.cancel_at_period_end ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Trial</p>
                          <p>{subscription.trial_end ? `Until ${formatDate(subscription.trial_end)}` : 'No trial'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoices Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <span>Invoice History ({invoices.length})</span>
                </div>

              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No invoices found</p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <div className="font-mono text-sm">{invoice.id}</div>
                          </TableCell>
                          <TableCell>
                            {formatDate(invoice.created)}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {formatAmount(invoice.amount_paid, invoice.currency)}
                              </p>
                              {invoice.amount_due > 0 && (
                                <p className="text-sm text-red-600">
                                  Due: {formatAmount(invoice.amount_due, invoice.currency)}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                              invoice.status === 'open' ? 'bg-blue-100 text-blue-800' :
                              invoice.status === 'void' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {invoice.period_start && invoice.period_end ? (
                                <>
                                  {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                                </>
                              ) : (
                                'One-time'
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end">
                              {(invoice as any).hosted_invoice_url && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open((invoice as any).hosted_invoice_url, '_blank')}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              {(invoice as any).invoice_pdf && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open((invoice as any).invoice_pdf, '_blank')}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Methods Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Payment Methods ({(customerDetails as any).payment_methods?.data?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!(customerDetails as any).payment_methods ||
               !(customerDetails as any).payment_methods.data ||
               (customerDetails as any).payment_methods.data.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No payment methods on file</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(customerDetails as any).payment_methods.data.map((paymentMethod: any) => (
                    <div key={paymentMethod.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-8 w-8 text-blue-600" />
                          <div>
                            <p className="font-medium">
                              **** **** **** {paymentMethod.card?.last4}
                            </p>
                            <p className="text-sm text-gray-600">
                              {paymentMethod.card?.brand?.toUpperCase()} •
                              Expires {paymentMethod.card?.exp_month}/{paymentMethod.card?.exp_year}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {paymentMethod.id === customerDetails.default_source && (
                            <Badge variant="secondary">Default</Badge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // TODO: Implement payment method management
                              toast({
                                title: "Payment Method",
                                description: "Payment method management will be implemented",
                              });
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-500">Payment Method ID</p>
                          <p className="font-mono">{paymentMethod.id}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Type</p>
                          <p className="capitalize">{paymentMethod.type}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Country</p>
                          <p className="uppercase">{paymentMethod.card?.country || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-500">Funding</p>
                          <p className="capitalize">{paymentMethod.card?.funding || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-10">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load Stripe customer data</p>
            <Button onClick={refreshAllData} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

// Create an alias for the enhanced Stripe section
export const UserStripeSection = StripeUserManagement;
